import { useEffect, useRef, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { DepositCTA } from '@/components/organisms/payments/DepositCTA';
import { PayabliDepositPayment } from '@/components/organisms/payments/PayabliDepositPayment';
import { useQuoteActions } from '../quoteActionContext';

type DepositSlotVariant = 'approvedAside' | 'sidebar';

// The deposit CTA renders at two sites — the approved-view aside and the
// itemized sidebar — that differ ONLY in the loading-Button className. The
// DepositCTA itself (and its disclosed, surcharge-aware total) is byte-identical
// at both, so the two amounts Stripe is handed can never disagree.
const LOADING_BUTTON_CLASS: Record<DepositSlotVariant, string> = {
  approvedAside: 'h-11 w-full rounded-xl bg-[#2B373F] text-white',
  sidebar:
    'h-12 w-full rounded-2xl bg-[#26343D] text-white shadow-[0_10px_24px_rgba(38,52,61,0.18)]',
};

// The collapsed Payabli CTA matches the same two sites, so switching processors
// does not change where the button sits or how big it is.
const PAYABLI_BUTTON_CLASS: Record<DepositSlotVariant, string> = {
  approvedAside: 'h-11 w-full rounded-xl bg-black text-white hover:bg-black/90',
  sidebar:
    'h-12 w-full rounded-2xl bg-black text-white shadow-[0_10px_24px_rgba(38,52,61,0.18)] hover:bg-black/90',
};

// Shown when the tenant has no usable online deposit path (an unsupported
// processor, or Payabli selected with its public config missing) — so we never
// render a Pay button that would 400 or route to the wrong processor.
function DepositUnavailableNotice() {
  const { documentLabel = 'Estimate' } = useQuoteActions();
  return (
    <p className="text-center text-sm text-muted-foreground" data-testid="deposit-unavailable">
      Online deposit payment is unavailable for this {documentLabel.toLowerCase()}. Please contact
      the business to pay your deposit.
    </p>
  );
}

// The deposit's bank payment failed or was returned, on either processor. The
// pay CTA renders right under it: the deposit is due again. `byCheck`: it was
// the customer's paper check, scanned by staff, that their bank returned.
function BankPaymentFailedNotice({
  byCheck,
  card = false,
}: {
  byCheck: boolean;
  /** A Payabli card attempt that settled as failed: not a bank transfer. */
  card?: boolean;
}) {
  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900"
      data-testid="deposit-failed"
    >
      <p className="font-semibold">Payment failed</p>
      <p className="mt-0.5 text-sm opacity-90">
        {byCheck
          ? 'Your check was returned by your bank. The deposit is still due — you can pay it below.'
          : card
            ? 'Your payment could not be completed. The deposit is still due — you can try again below.'
            : 'Your bank transfer could not be completed. The deposit is still due — you can try again below with another payment method.'}
      </p>
    </div>
  );
}

export function DepositSlot({ variant }: { variant: DepositSlotVariant }) {
  const { depositAutoOpen } = useQuoteActions();
  // Approve and pay: the payment opened itself after approval. Bring it into
  // view and put focus on its first action — the method chooser, or the
  // Payabli form.
  const slotRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!depositAutoOpen) return;
    const slot = slotRef.current;
    if (!slot) return;
    slot.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    slot.querySelector<HTMLElement>('button:not([disabled])')?.focus({ preventScroll: true });
  }, [depositAutoOpen]);
  return (
    <div ref={slotRef} className="w-full" data-testid="deposit-slot">
      <DepositSlotBody variant={variant} autoOpen={depositAutoOpen} />
    </div>
  );
}

function DepositSlotBody({
  variant,
  autoOpen,
}: {
  variant: DepositSlotVariant;
  autoOpen: boolean;
}): ReactNode {
  const {
    redirectingDeposit,
    isSettlingDeposit,
    estimate,
    // The figure the server will charge: the selection-priced deposit while a
    // required deposit is paid before approval (staff in person), the stored
    // one after approval.
    depositChargeAmount,
    payerContactId,
    depositSurcharge,
    depositMutation,
    pendingDepositMethod,
    startDeposit,
    payment,
    payDepositWithPayabli,
    depositAchSettling,
    depositAttemptRail,
    depositAchFailed,
    depositClearing,
    depositFailed,
    depositByCheck,
  } = useQuoteActions();
  // A bank payment that failed: one this page watched fail, or one that was
  // returned after the customer left (the estimate may already be approved).
  const bankPaymentFailed = depositAchFailed || depositFailed;

  // A bank payment the processor accepted: the deposit is paid as far as this
  // page is concerned, and the money lands in days. Owns the slot on either
  // processor, so nothing here offers to take the deposit a second time.
  if (depositClearing) {
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
        data-testid="deposit-clearing"
      >
        {depositByCheck ? (
          <>
            <p className="font-semibold">Check pending</p>
            <p className="mt-0.5 text-sm opacity-90">
              Your check was received. It usually clears in 3–5 business days — no further action is
              needed.
            </p>
          </>
        ) : estimate.depositClearingRail === 'BANK' ? (
          <>
            <p className="font-semibold">Pending ACH</p>
            <p className="mt-0.5 text-sm opacity-90">
              Your bank payment was received. It usually settles in 3–5 business days — no further
              action is needed.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold">Payment pending</p>
            <p className="mt-0.5 text-sm opacity-90">
              Your payment was received and is being confirmed — no further action is needed.
            </p>
          </>
        )}
      </div>
    );
  }

  // Only the Stripe path leaves the page, so only it has a redirect state.
  if (redirectingDeposit || isSettlingDeposit) {
    return (
      <Button className={LOADING_BUTTON_CLASS[variant]} disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {redirectingDeposit ? 'Redirecting to checkout…' : 'Finalizing deposit…'}
      </Button>
    );
  }

  // Branch on the processor EXPLICITLY. Never fall through to Stripe for an
  // unknown processor: on a Payabli tenant the Stripe deposit endpoint 400s,
  // which is exactly the bug this branch exists to fix.
  if (payment?.processor === 'payabli') {
    // Server-derived ACH states own the slot: a reload during an unsettled
    // bank transfer resumes the submitted banner (never a live pay CTA over
    // money in flight), and a watched failure renders a retryable notice.
    if (depositAchSettling) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          <p className="font-semibold">Payment submitted</p>
          <p className="mt-0.5 text-sm opacity-90">
            {depositAttemptRail === 'BANK'
              ? 'Your bank transfer is processing. The deposit will be confirmed automatically once it clears — no further action is needed.'
              : 'Your payment is being confirmed. The deposit will update automatically — no further action is needed.'}
          </p>
        </div>
      );
    }
    return payment.payabli ? (
      <div className="flex w-full flex-col gap-3">
        {bankPaymentFailed ? (
          <BankPaymentFailedNotice
            byCheck={depositFailed && depositByCheck}
            card={depositAchFailed && !depositFailed && depositAttemptRail === 'CARD'}
          />
        ) : null}
        <PayabliDepositPayment
          depositAmount={depositChargeAmount}
          config={payment.payabli}
          contactId={payerContactId}
          customerName={estimate.customer?.name}
          customerEmail={estimate.customer?.email}
          onCharge={payDepositWithPayabli}
          surcharge={depositSurcharge}
          buttonClassName={PAYABLI_BUTTON_CLASS[variant]}
          defaultOpen={autoOpen}
        />
      </div>
    ) : (
      <DepositUnavailableNotice />
    );
  }

  // Stripe, or a legacy/absent payment block — back-compat with payloads that
  // predate it. An absent block is the ONLY thing that means "assume Stripe": a
  // config the server could not resolve arrives as processor 'unknown' and
  // falls through to the notice below, never to this CTA.
  if (!payment || payment.processor === 'stripe') {
    const cta = (
      <DepositCTA
        depositAmount={depositChargeAmount}
        surcharge={depositSurcharge}
        busy={depositMutation.isPending}
        pendingMethod={pendingDepositMethod}
        onPay={startDeposit}
      />
    );
    return depositFailed ? (
      <div className="flex w-full flex-col gap-3">
        <BankPaymentFailedNotice byCheck={depositByCheck} />
        {cta}
      </div>
    ) : (
      cta
    );
  }

  // 'none' or an unsupported processor → no online deposit path.
  return <DepositUnavailableNotice />;
}
